<template>
  <div class="min-h-screen h-screen flex overflow-hidden">
    <!-- Left Side - Form -->
    <div class="w-full lg:w-1/2 flex flex-col bg-white dark:bg-corporate-950">
      <div class="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-20 py-4">
        <div class="w-full max-w-md mx-auto">
          <!-- Header with Logo (Centered) -->
          <div class="flex flex-col items-center mb-5">
            <router-link to="/" class="flex items-center justify-center mb-4">
              <img src="/images/logo-icon.png" alt="Dashboard" class="h-12 w-auto" />
            </router-link>
            <router-link to="/login" class="text-sm text-corporate-500 hover:text-corporate-700 dark:text-corporate-400 transition-colors">
              Déjà inscrit ? Se connecter
            </router-link>
          </div>

          <!-- Title (Centered) -->
          <div class="mb-4 text-center">
            <h1 class="text-xl font-semibold text-corporate-900 dark:text-white">Créer un compte</h1>
            <p class="text-sm text-corporate-500 dark:text-corporate-400 mt-1">Rejoignez notre plateforme de gestion</p>
          </div>

          <!-- Error Alert -->
          <div v-if="error" class="mb-3 p-2.5 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4 text-error-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              <p class="text-sm text-error-700 dark:text-error-400">{{ error }}</p>
            </div>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-3">
            <!-- Name Fields -->
            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label for="firstName" class="block text-xs font-medium text-corporate-700 dark:text-corporate-300 mb-1">
                  Prénom <span class="text-error-500">*</span>
                </label>
                <input
                  v-model="form.firstName"
                  type="text"
                  id="firstName"
                  placeholder="Jean"
                  autocomplete="given-name"
                  class="w-full h-9 px-3 rounded-lg border border-corporate-300 bg-white text-sm text-corporate-900 placeholder:text-corporate-400 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
                />
              </div>
              <div>
                <label for="lastName" class="block text-xs font-medium text-corporate-700 dark:text-corporate-300 mb-1">
                  Nom <span class="text-error-500">*</span>
                </label>
                <input
                  v-model="form.lastName"
                  type="text"
                  id="lastName"
                  placeholder="Dupont"
                  autocomplete="family-name"
                  class="w-full h-9 px-3 rounded-lg border border-corporate-300 bg-white text-sm text-corporate-900 placeholder:text-corporate-400 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
                />
              </div>
            </div>

            <!-- Email & Phone -->
            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label for="email" class="block text-xs font-medium text-corporate-700 dark:text-corporate-300 mb-1">
                  Email <span class="text-error-500">*</span>
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  id="email"
                  placeholder="vous@exemple.com"
                  autocomplete="email"
                  class="w-full h-9 px-3 rounded-lg border border-corporate-300 bg-white text-sm text-corporate-900 placeholder:text-corporate-400 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
                />
              </div>
              <div>
                <label for="phone" class="block text-xs font-medium text-corporate-700 dark:text-corporate-300 mb-1">
                  Téléphone
                </label>
                <input
                  v-model="form.phone"
                  type="tel"
                  id="phone"
                  placeholder="+33 6 00 00 00 00"
                  autocomplete="tel"
                  class="w-full h-9 px-3 rounded-lg border border-corporate-300 bg-white text-sm text-corporate-900 placeholder:text-corporate-400 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
                />
              </div>
            </div>

            <!-- Company -->
            <div>
              <label for="companyName" class="block text-xs font-medium text-corporate-700 dark:text-corporate-300 mb-1">
                Entreprise
              </label>
              <input
                v-model="form.companyName"
                type="text"
                id="companyName"
                placeholder="Ma Société (optionnel)"
                autocomplete="organization"
                class="w-full h-9 px-3 rounded-lg border border-corporate-300 bg-white text-sm text-corporate-900 placeholder:text-corporate-400 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
              />
            </div>

            <!-- Passwords on same line -->
            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label for="password" class="block text-xs font-medium text-corporate-700 dark:text-corporate-300 mb-1">
                  Mot de passe <span class="text-error-500">*</span>
                </label>
                <div class="relative">
                  <input
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    placeholder="Min. 8 caractères"
                    autocomplete="new-password"
                    class="w-full h-9 px-3 pr-9 rounded-lg border border-corporate-300 bg-white text-sm text-corporate-900 placeholder:text-corporate-400 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
                  />
                  <button type="button" @click="showPassword = !showPassword" class="absolute right-2.5 top-1/2 -translate-y-1/2 text-corporate-400 hover:text-corporate-600">
                    <svg v-if="showPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  </button>
                </div>
              </div>
              <div>
                <label for="confirmPassword" class="block text-xs font-medium text-corporate-700 dark:text-corporate-300 mb-1">
                  Confirmer <span class="text-error-500">*</span>
                </label>
                <input
                  v-model="form.confirmPassword"
                  :type="showPassword ? 'text' : 'password'"
                  id="confirmPassword"
                  placeholder="Répéter"
                  autocomplete="new-password"
                  :class="[
                    'w-full h-9 px-3 rounded-lg border bg-white text-sm text-corporate-900 placeholder:text-corporate-400 focus:outline-none focus:ring-2 dark:bg-corporate-900 dark:text-white',
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? 'border-error-300 focus:border-error-500 focus:ring-error-500/20'
                      : 'border-corporate-300 focus:border-accent-500 focus:ring-accent-500/20 dark:border-corporate-700'
                  ]"
                />
              </div>
            </div>

            <!-- Password Strength (compact) -->
            <div v-if="form.password" class="flex items-center gap-2">
              <div class="flex gap-0.5 flex-1">
                <div v-for="i in 5" :key="i" :class="['h-1 flex-1 rounded-full', i <= passwordStrength.score ? passwordStrength.color : 'bg-corporate-200 dark:bg-corporate-700']" />
              </div>
              <span class="text-xs text-corporate-500">{{ passwordStrength.label }}</span>
            </div>

            <!-- Terms -->
            <label class="flex items-start gap-2 cursor-pointer">
              <input v-model="form.acceptTerms" type="checkbox" class="mt-0.5 w-4 h-4 rounded border-corporate-300 text-accent-600 focus:ring-accent-500 dark:border-corporate-600 dark:bg-corporate-800" />
              <span class="text-xs text-corporate-600 dark:text-corporate-400 leading-tight">
                J'accepte les <a href="/legal/cgu" class="text-accent-600 hover:underline">CGU</a> et la 
                <a href="/legal/confidentialite" class="text-accent-600 hover:underline">politique de confidentialité</a>
              </span>
            </label>

            <!-- Recovery Key Acknowledgment (Critical Legal Requirement) -->
            <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700">
              <label class="flex items-start gap-2 cursor-pointer">
                <input v-model="form.acceptRecoveryKeyRisk" type="checkbox" class="mt-0.5 w-4 h-4 rounded border-red-400 text-red-600 focus:ring-red-500 dark:border-red-600 dark:bg-corporate-800" />
                <span class="text-xs text-red-700 dark:text-red-300 leading-tight font-medium">
                  ⚠️ Je comprends que mes données sont chiffrées et que <strong>je suis seul responsable de la sauvegarde de ma clé de récupération</strong>. 
                  En cas de perte, mes données seront <strong>définitivement irrécupérables</strong> et LAUMERIS ne pourra pas les restaurer.
                </span>
              </label>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="isLoading || !form.acceptTerms || !form.acceptRecoveryKeyRisk"
              class="w-full h-10 flex items-center justify-center gap-2 rounded-lg border-2 border-corporate-800 bg-transparent hover:bg-corporate-800 text-corporate-800 hover:text-white dark:border-0 dark:bg-[#1E3A5F] dark:hover:bg-[#152238] dark:text-white text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-corporate-400/50 dark:focus:ring-[#1E3A5F]/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Créer mon compte
            </button>
          </form>

          <!-- Footer -->
          <p class="mt-4 text-center text-xs text-corporate-400">
            &copy; {{ new Date().getFullYear() }} Laumeris
          </p>
        </div>
      </div>
    </div>

    <!-- Right Panel - Animated Partnership (Theme Adaptive) -->
    <div class="hidden lg:flex lg:w-1/2 bg-white dark:bg-corporate-950 items-center justify-center relative overflow-hidden">
      
      <!-- Animated Background Orbs -->
      <div class="absolute inset-0 overflow-hidden">
        <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-[#1E3A5F]/10 dark:bg-[#1E3A5F]/20 rounded-full blur-[120px] animate-pulse-slow" />
        <div class="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#C9B896]/5 dark:bg-[#C9B896]/10 rounded-full blur-[100px] animate-pulse-slower" />
      </div>

      <!-- Content Container -->
      <div class="relative z-10 flex flex-col items-center justify-center h-full px-12 py-16">
        
        <!-- Animated Logos -->
        <div class="flex items-center gap-6 mb-14 animate-fade-in-up" style="animation-delay: 0.2s;">
          <!-- Logo 1 -->
          <div class="relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-[#C9B896]/20 to-[#1E3A5F]/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div class="relative w-24 h-24 bg-corporate-100/50 dark:bg-white/[0.02] backdrop-blur-xl border border-corporate-200 dark:border-white/[0.08] rounded-2xl flex items-center justify-center transition-transform duration-500 hover:scale-105">
              <img src="/images/logo-icon.png" alt="Partner 1" class="h-14 w-auto" />
            </div>
          </div>

          <!-- Animated Connector -->
          <div class="flex flex-col items-center gap-1 animate-fade-in" style="animation-delay: 0.5s;">
            <div class="w-[1px] h-4 bg-gradient-to-b from-transparent to-[#C9B896]/40 dark:to-[#C9B896]/30" />
            <div class="w-3 h-3 border border-[#C9B896]/50 dark:border-[#C9B896]/40 rotate-45" />
            <div class="w-[1px] h-4 bg-gradient-to-t from-transparent to-[#C9B896]/40 dark:to-[#C9B896]/30" />
          </div>

          <!-- Logo 2 -->
          <div class="relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-[#1E3A5F]/20 to-[#C9B896]/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div class="relative w-24 h-24 bg-corporate-100/50 dark:bg-white/[0.02] backdrop-blur-xl border border-corporate-200 dark:border-white/[0.08] rounded-2xl flex items-center justify-center transition-transform duration-500 hover:scale-105">
              <img src="/images/logo-lmw.png" alt="Partner 2" class="h-16 w-auto" />
            </div>
          </div>
        </div>

        <!-- Title with reveal animation -->
        <div class="text-center mb-10 animate-fade-in-up" style="animation-delay: 0.4s;">
          <div class="inline-flex items-center gap-3 mb-6">
            <div class="w-8 h-[1px] bg-gradient-to-r from-transparent to-[#C9B896]/60 dark:to-[#C9B896]/50" />
            <span class="text-[11px] text-[#C9B896] dark:text-[#C9B896]/60 uppercase tracking-[0.4em] font-light">Partenariat</span>
            <div class="w-8 h-[1px] bg-gradient-to-l from-transparent to-[#C9B896]/60 dark:to-[#C9B896]/50" />
          </div>
          <h2 class="text-3xl font-extralight text-corporate-800 dark:text-white/90 tracking-wide mb-4">
            Plateforme de Gestion
          </h2>
          <p class="text-sm text-corporate-500 dark:text-white/40 max-w-sm leading-relaxed">
            Une alliance d'excellence au service de votre entreprise
          </p>
        </div>

        <!-- Animated Features -->
        <div class="flex gap-12 mb-14 animate-fade-in-up" style="animation-delay: 0.6s;">
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Sécurisé</p>
          </div>
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Performant</p>
          </div>
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Innovant</p>
          </div>
        </div>

        <!-- Bottom signature -->
        <div class="text-center animate-fade-in" style="animation-delay: 0.8s;">
          <div class="inline-flex items-center gap-2">
            <div class="w-1 h-1 rounded-full bg-[#C9B896]/60 dark:bg-[#C9B896]/40" />
            <p class="text-[10px] text-corporate-400 dark:text-white/20 uppercase tracking-[0.3em] font-light">Excellence & Confiance</p>
            <div class="w-1 h-1 rounded-full bg-[#C9B896]/60 dark:bg-[#C9B896]/40" />
          </div>
        </div>
      </div>

      <!-- Corner accents -->
      <div class="absolute top-0 left-0 w-32 h-32">
        <div class="absolute top-8 left-8 w-[1px] h-16 bg-gradient-to-b from-corporate-200 dark:from-white/10 to-transparent" />
        <div class="absolute top-8 left-8 w-16 h-[1px] bg-gradient-to-r from-corporate-200 dark:from-white/10 to-transparent" />
      </div>
      <div class="absolute bottom-0 right-0 w-32 h-32">
        <div class="absolute bottom-8 right-8 w-[1px] h-16 bg-gradient-to-t from-corporate-200 dark:from-white/10 to-transparent" />
        <div class="absolute bottom-8 right-8 w-16 h-[1px] bg-gradient-to-l from-corporate-200 dark:from-white/10 to-transparent" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  password: '',
  confirmPassword: '',
  acceptTerms: false,
  acceptRecoveryKeyRisk: false
})

const error = ref('')
const showPassword = ref(false)
const isLoading = ref(false)

const passwordStrength = computed(() => {
  const password = form.password
  if (!password) return { score: 0, label: '', color: '' }

  let score = 0
  if (password.length >= 8) score++
  if (password.length >= 12) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^a-zA-Z0-9]/.test(password)) score++

  const levels = [
    { label: 'Très faible', color: 'bg-error-500' },
    { label: 'Faible', color: 'bg-error-400' },
    { label: 'Moyen', color: 'bg-warning-500' },
    { label: 'Fort', color: 'bg-success-400' },
    { label: 'Très fort', color: 'bg-success-500' }
  ]

  return { score, ...levels[Math.min(score, 4)] }
})

const handleSubmit = async () => {
  error.value = ''
  isLoading.value = true

  try {
    if (!form.firstName || !form.lastName) {
      error.value = 'Veuillez entrer votre nom complet'
      isLoading.value = false
      return
    }
    if (!form.email) {
      error.value = 'L\'email est requis'
      isLoading.value = false
      return
    }
    if (!form.password) {
      error.value = 'Le mot de passe est requis'
      isLoading.value = false
      return
    }
    if (form.password.length < 8) {
      error.value = 'Le mot de passe doit contenir au moins 8 caractères'
      isLoading.value = false
      return
    }
    if (form.password !== form.confirmPassword) {
      error.value = 'Les mots de passe ne correspondent pas'
      isLoading.value = false
      return
    }
    if (!form.acceptTerms) {
      error.value = 'Vous devez accepter les conditions d\'utilisation'
      isLoading.value = false
      return
    }
    if (!form.acceptRecoveryKeyRisk) {
      error.value = 'Vous devez confirmer avoir compris les risques liés à la perte de votre clé de récupération'
      isLoading.value = false
      return
    }

    const result = await authStore.register({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone || undefined,
      companyName: form.companyName || undefined,
      password: form.password
    })

    if (result.success) {
      toast.success('Compte créé', 'Bienvenue sur Laumeris !')
      router.push('/dashboard')
    } else {
      error.value = result.error || 'Erreur lors de l\'inscription'
    }
  } catch (e: any) {
    error.value = e.message || 'Une erreur est survenue'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
/* Fade in up animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Simple fade in */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slow pulse for background orbs */
@keyframes pulseSlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
}

@keyframes pulseSlower {
  0%, 100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.15);
  }
}

.animate-fade-in-up {
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;
}

.animate-fade-in {
  opacity: 0;
  animation: fadeIn 0.6s ease-out forwards;
}

.animate-pulse-slow {
  animation: pulseSlow 8s ease-in-out infinite;
}

.animate-pulse-slower {
  animation: pulseSlower 10s ease-in-out infinite;
  animation-delay: 2s;
}
</style>
